import { reactive } from "vue";
import { AppConstants } from "@/constants/AppConstants";

export interface UserState {
  userId: string;
  agCode: string;
  agName: string;
  emailAddress: string;
  pseudoAgCode: string;
  pseudoAgName: string;
  pseudoUserId: string;
  pseudoDaihyoCode: string;
  isLoggedIn: boolean;
  isAdmin: boolean;
  isKaunet: boolean;
  invoiceNo: string;
  roleId: string;
  itakumotoAgCode: string;
  daihyoCode: string;
  saiitakuFlg: boolean;
  userName: string;
  companyName: string;
  sectionName: string;
  bikou1: string;
  bikou2: string;
  isBaseLogin: boolean;
}

const state = reactive<UserState>({
  userId: "", //ユーザーID
  agCode: "", //AGコード
  agName: "", //AG名称
  emailAddress: "",
  pseudoAgCode: "", //疑似ログインAGコード
  pseudoAgName: "", //疑似ログインAG名称
  pseudoUserId: "", //疑似ログインユーザーID
  pseudoDaihyoCode: "", //疑似ログイン代表コード
  isLoggedIn: false,
  isAdmin: false,
  isKaunet: false,
  invoiceNo: "",
  roleId: "",
  itakumotoAgCode: "",
  daihyoCode: "",
  saiitakuFlg: false,
  userName: "",
  companyName: "",
  sectionName: "",
  bikou1: "",
  bikou2: "",
  isBaseLogin: false,
});

export function useUserStore() {
  const setUser = (
    userId: string,
    agCode: string,
    agName: string,
    isAdmin: boolean,
    invoiceNo: string,
    emailAddress: string,
    roleId: string,
    itakumotoAgCode: string,
    daihyoCode: string,
    saiitakuFlg: boolean,
    userName: string,
    companyName: string,
    sectionName: string,
    bikou1: string,
    bikou2: string,
    isBaseLogin: boolean,
    isKaunet: boolean
  ) => {
    state.userId = userId;
    state.agCode = agCode;
    state.agName = agName;
    // 疑似ログインIDは空にする
    state.pseudoAgCode = "";
    state.pseudoAgName = "";
    state.pseudoUserId = "";
    state.pseudoDaihyoCode = "";
    // ログインフラグON
    state.isLoggedIn = true;
    // 管理者権限有無
    state.isAdmin = isAdmin;
    // インボイス番号
    state.invoiceNo = invoiceNo;
    // Mailアドレス
    state.emailAddress = emailAddress;
    // ロールID
    state.roleId = roleId;
    // 委託元AGコード
    state.itakumotoAgCode = itakumotoAgCode;
    // 代表コード
    state.daihyoCode = daihyoCode;
    // 再委託フラグ
    state.saiitakuFlg = saiitakuFlg;
    // 担当者名
    state.userName = userName;
    // 会社名
    state.companyName = companyName;
    // 部署名
    state.sectionName = sectionName;
    // 備考１
    state.bikou1 = bikou1;
    // 備考２
    state.bikou2 = bikou2;
    // ベースログインフラグ
    state.isBaseLogin = isBaseLogin;
    // カウネットフラグ
    state.isKaunet = isKaunet;
    // セッションストレージにユーザー情報を保存
    saveToLocalStorage();
  };

  const logoutUser = () => {
    // ローカルストレージからユーザー情報を削除
    localStorage.removeItem(AppConstants.USER_SESSION_NAME);
    // ローカルストレージからグローバルナビデータを削除
    localStorage.removeItem(AppConstants.GROVAL_NAVI_SESSION_NAME);
    // ローカルストレージから権限情報を削除
    localStorage.removeItem(AppConstants.PARMITION_SESSION_NAME);
    // ローカルストレージからサイト内検索のカテゴリ情報を削除
    localStorage.removeItem(AppConstants.SITE_SERCH_CATEGORY_SESSION_NAME);
    // ローカルストレージからトークン情報を削除
    localStorage.removeItem(AppConstants.TOKEN_SESSION_NAME);
    // ローカルストレージからインセンティブ情報を削除
    localStorage.removeItem(AppConstants.INCENTIVE_INFO_SESSION_NAME);
  };

  const saveToLocalStorage = () => {
    localStorage.setItem(AppConstants.USER_SESSION_NAME, JSON.stringify(state));
  };

  const setPseudoData = (
    pseudoAgCode: string,
    pseudoAgName: string,
    pseudoUserId: string,
    pseudoDaihyoCode: string
  ) => {
    loadFromLocalStorage();

    state.pseudoAgCode = pseudoAgCode;
    state.pseudoAgName = pseudoAgName;
    state.pseudoUserId = pseudoUserId;
    state.pseudoDaihyoCode = pseudoDaihyoCode;

    localStorage.setItem(AppConstants.USER_SESSION_NAME, JSON.stringify(state));
  };

  const removePseudoData = () => {
    loadFromLocalStorage();

    state.pseudoAgCode = "";
    state.pseudoAgName = "";
    state.pseudoUserId = "";
    state.pseudoDaihyoCode = "";

    localStorage.setItem(AppConstants.USER_SESSION_NAME, JSON.stringify(state));
  };

  const loadFromLocalStorage = () => {
    const storedData = localStorage.getItem(AppConstants.USER_SESSION_NAME);
    if (storedData) {
      const userInfo = JSON.parse(storedData);
      state.userId = userInfo.userId;
      state.agCode = userInfo.agCode;
      state.agName = userInfo.agName;
      state.pseudoAgCode = userInfo.pseudoAgCode;
      state.isLoggedIn = userInfo.isLoggedIn;
      state.pseudoAgName = userInfo.pseudoAgName;
      state.pseudoUserId = userInfo.pseudoUserId;
      state.pseudoDaihyoCode = userInfo.pseudoDaihyoCode;
      state.isLoggedIn = userInfo.isLoggedIn;
      state.isAdmin = userInfo.isAdmin;
      state.invoiceNo = userInfo.invoiceNo;
      state.emailAddress = userInfo.emailAddress;
      state.roleId = userInfo.roleId;
      state.itakumotoAgCode = userInfo.itakumotoAgCode;
      state.daihyoCode = userInfo.daihyoCode;
      state.saiitakuFlg = userInfo.saiitakuFlg;
      state.userName = userInfo.userName;
      state.companyName = userInfo.companyName;
      state.sectionName = userInfo.sectionName;
      state.bikou1 = userInfo.bikou1;
      state.bikou2 = userInfo.bikou2;
      state.isBaseLogin = userInfo.isBaseLogin;
      state.isKaunet = userInfo.isKaunet;
    }
  };

  return {
    state,
    setUser,
    logoutUser,
    setPseudoData,
    removePseudoData,
    loadFromLocalStorage,
  };
}
